import { FileStore } from "@pdfking/compatibility"
import type { StreamInput } from "@pdfking/io"
import { StreamProcess } from "@pdfking/io"
import type { TrackingStatus } from "@pdfking/planner"
import type { Bytes, FileName } from "@pdfking/structs"
import { GuidToString, UUID, Void } from "@pdfking/tfunc"

import type { PdfEngineOptions } from "#public"
import type { RemoteFile } from "#public"
import type { GeneratedContent } from "#public"

import { VirtualFileType } from "../types/files/VirtualFileType"
import { TrackedTaskName } from "../types/tasks/TrackedTaskName"

/** The stream, watched on its way into the store.
 *
 * Nothing here consumes it twice: the walk that flattens whatever a caller
 * sent is the walk that counts it, and every part comes out of it as it went
 * in — bytes stay bytes, a blob stays a blob, unread. A caller hears about
 * this task the moment its content is big enough to be worth waiting for, and
 * hears it is over when the last part has gone past. */
async function* Tracked(
  input: StreamInput,
  threshold: Bytes,
  emit: (task: TrackingStatus) => void
): AsyncGenerator<StreamInput> {
  const status = (done: 0 | 1): TrackingStatus => ({
    done,
    name: TrackedTaskName.GENERATE_FILE,
    total: 1,
  })

  let size = 0
  let started = false

  // The size is the only thing taken from a part; what comes back is the part
  // itself, on its way to the store.
  const count = <T>(part: T, bytes: number): T => {
    size += bytes
    return part
  }

  for await (const part of StreamProcess<StreamInput>(
    input,
    s => count(s, s.length),
    b => count(b, b.length),
    b => count(b, b.size)
  )) {
    if (!started && size > threshold) {
      started = true
      emit(status(0))
    }

    yield part
  }

  // Nothing was announced, so there is nothing to close: below the threshold
  // this task is not worth a caller's attention at all.
  if (started) emit(status(1))
}

/** Content, written to the store and named by the url it answers to there.
 *
 * What comes back is a remote file like any other: the store's own url is its
 * mirror, so anything holding it reads the bytes through the loader rather
 * than through the engine, and there is nothing left to find out about them —
 * the write already said everything `info` holds, which is what spares a
 * reader the download a mirror it knew nothing about would have cost.
 *
 * The name is the FileContentGUID of the file the caller makes, when it has
 * one: a task re-executed after its bytes were evicted writes them under the
 * same name, so every stale
 * reference embedded in a cached response is good again the moment the re-run
 * lands — recovery is a property of the naming rather than a repair anybody
 * performs. A caller making no file — an upload, a paste — has no identity to
 * offer, and gets a uuid: two writes are two entries, and bytes that happen
 * to match are still somebody else's file. Never a hash of the content: what
 * a file is called says where it is kept, not what is in it. */
export const GenerateFile = async (
  { file_cache, tracking_chunk_size }: PdfEngineOptions,
  stream: StreamInput,
  { type, emit = Void, guid = GuidToString(UUID()), file_meta = {} }: GeneratedContent
): Promise<RemoteFile> => {
  // The name is settled before a byte is written, which is what lets the
  // write be one pass over the stream with nothing to hash and nothing to
  // rename afterwards.
  const store = FileStore(file_cache)

  // One pass, and the store is what it goes into: the write says how big the
  // content is and when it landed, so nothing here sizes it a second time —
  // that is the whole of `info`, kept as the store gave it, except the etag,
  // which is the name we gave it rather than whatever the storage underneath
  // calls a version.
  const info = await store.set(
    guid as string as FileName,
    Tracked(stream, tracking_chunk_size, emit),
    type
  )

  return {
    enum: VirtualFileType.REMOTE_FILE,
    file_meta,
    guid,
    info: { ...info, etag: guid },
    mirrors: [store.url(guid as string as FileName)],
    type,
  }
}
